import TopNavigation from '@cloudscape-design/components/top-navigation';
import prodLogo from '../images/GRU-Icon-2.svg';
import betaLogo from '../images/GRU-Icon-2-Beta.svg';
import gammaLogo from '../images/GRU-Icon-2-Gamma.svg';
import { useAuth } from './providers/AuthProvider';
import { Button, Hotspot, TextContent } from '@cloudscape-design/components';
import { useNavigate } from 'react-router';
import { usePreferences } from './providers/PreferencesProvider';
import { LANGUAGES_TO_COUNTRY } from 'src/constants';
import { useTranslate } from 'src/i18n/useTranslate';
import { HOT_SPOTS } from 'src/tutorials/enums';
import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from '@amzn/katal-metrics';
import { PAGE } from 'src/pages/enums';
import { useState } from 'react';
import FeedbackModal from 'src/components/FeedbackModal';
import { Stage, getStage } from '../helpers';
import '../styles/navigation.scss';
import { SearchAutoSuggest } from 'src/components/SearchAutoSuggest';
import { useSearchParams } from 'react-router-dom';
import { SearchBar } from './kendra-search/search/searchBar/SearchBar';

interface NavigationProps {
    setPreferencesOpen: (isOpen: boolean) => void;
    setExploreModalOpen: (isOpen: boolean) => void;
}

const Navigation = ({
    setPreferencesOpen,
    setExploreModalOpen,
}: NavigationProps) => {
    const t = useTranslate();

    const { user } = useAuth();
    const { userPreferences } = usePreferences();
    const navigate = useNavigate();

    const isOnHomepage = location.pathname === '/';
    const countryAbbreviation =
        LANGUAGES_TO_COUNTRY[userPreferences.language.path] || 'us';

    const stage = getStage();
    const useKendraSearch = [Stage.BETA, Stage.GAMMA, Stage.TEST].includes(
        stage
    );

    const [searchParams] = useSearchParams();
    const searchInput = searchParams.get('q') || '';

    const [feedbackModalOpen, setFeedbackModalOpen] = useState<boolean>(false);
    const [legacySearchInput, setLegacySearchInput] = useState<string>('');

    const handleSearch = (query: string) => {
        navigate(`/search?q=${query}`);
    };

    const clickExploreButtonMetricsPublisher =
        initialMetricsPublisher.newChildActionPublisherForMethod(
            'ClickExploreButton'
        );

    const clickExploreButton = () => {
        setExploreModalOpen(true);

        clickExploreButtonMetricsPublisher.publish(
            new KatalMetrics.Metric.String(
                'gru.explore.click',

                user?.hashedUserId || ''
            )
        );
    };

    let logo = prodLogo;
    if (stage == Stage.TEST) {
        logo = betaLogo;
    } else if (stage == Stage.BETA) {
        logo = betaLogo;
    } else if (stage == Stage.GAMMA) {
        logo = gammaLogo;
    }

    return (
        <div id="top-navigation">
            <TopNavigation
                identity={{
                    href: '/',
                    title: 'GRU',
                    logo: {
                        src: logo,
                        alt: 'Global Robotics University',
                    },
                }}
                utilities={[
                    {
                        type: 'button',
                        text: `${userPreferences?.language.name} - ${userPreferences?.role.name}`,
                        iconUrl: `https://d1uznvntk80v7s.cloudfront.net/flags/${countryAbbreviation}.svg`,
                        onClick: () => setPreferencesOpen(true),
                    },
                    {
                        type: 'button',
                        text: t('favorite-breadcrumb', 'My Favorites'),
                        iconName: 'heart',
                        onClick: () => navigate(`/${PAGE.FAVORITES}`),
                    },
                    {
                        type: 'button',
                        text: t('give_feedback', 'Give Feedback'),
                        onClick: () => setFeedbackModalOpen(true),
                    },
                    {
                        type: 'button',
                        text: user?.name,
                        iconName: 'user-profile',
                    },
                ]}
                search={
                    <div id="search-filters-navigation">
                        {!isOnHomepage && (
                            <TextContent>
                                <Button
                                    data-explore-button
                                    onClick={() => {
                                        clickExploreButton();
                                    }}
                                >
                                    <h4 style={{ color: 'white' }}>
                                        {t('header_explore_label', 'Explore')}
                                    </h4>
                                </Button>
                            </TextContent>
                        )}
                        <div data-navigation-search-input>
                            {useKendraSearch ? (
                                <SearchBar
                                    onSubmit={(query, page) => {
                                        handleSearch(query);
                                    }}
                                    initialValue={searchInput}
                                />
                            ) : (
                                <SearchAutoSuggest
                                    value={legacySearchInput}
                                    setValue={setLegacySearchInput}
                                    placeholder={t(
                                        'search_placeholder_text_type_a_keyword',
                                        'Type a keyword to search GRU'
                                    )}
                                />
                            )}
                        </div>
                    </div>
                }
            />
            <div className="navigation-hotspot-wrapper">
                <div className="navigation-hotspot-container">
                    <Hotspot
                        hotspotId={HOT_SPOTS.TOP_NAV_BAR_WELCOME}
                        direction="bottom"
                        side="right"
                    />
                    <Hotspot
                        hotspotId={HOT_SPOTS.TOP_NAV_BAR_USER_PREFERENCES}
                        direction="bottom"
                        side="right"
                    />
                    <Hotspot
                        hotspotId={HOT_SPOTS.TOP_NAV_BAR_UPDATED_PREFERENCES}
                        direction="bottom"
                        side="right"
                    />
                </div>
            </div>

            <FeedbackModal
                feedbackModalOpen={feedbackModalOpen}
                setFeedbackModalOpen={setFeedbackModalOpen}
            />
        </div>
    );
};

export default Navigation;
